/**
 * Process Monitor Store
 *
 * Tracks long-running background processes (BPMN generation, etc.)
 * Provides real-time status updates and elapsed time calculation
 * Persists to localStorage to survive page reloads
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ProcessType = 'bpmn' | 'plan' | 'script' | 'lumajson';
export type ProcessStatus = 'queued' | 'processing' | 'completing' | 'completed' | 'failed' | 'timeout';

export interface ModelInfo {
  provider: 'openai' | 'anthropic';
  selectedModel: string;  // What user selected (e.g., "gpt-5")
  actualModel?: string;   // What's actually running (e.g., "o1-preview-2024-09-12")
}

export interface ProcessInfo {
  id: string;
  type: ProcessType;
  skillId: string;
  skillName: string;
  startedAt: number;
  status: ProcessStatus;
  modelInfo?: ModelInfo;
  error?: string;
  warnings?: string[];
}

interface ProcessMonitorState {
  // Active processes currently running
  activeProcesses: Map<string, ProcessInfo>;

  // Recently completed processes (keep last 5)
  completedProcesses: ProcessInfo[];

  // UI state
  isExpanded: boolean;

  // Actions
  addProcess: (info: ProcessInfo) => void;
  updateProcess: (id: string, updates: Partial<ProcessInfo>) => void;
  removeProcess: (id: string) => void;
  getElapsedTime: (id: string) => number;
  toggleExpanded: () => void;
  clearCompleted: () => void;
}

export const useProcessMonitor = create<ProcessMonitorState>()(
  persist(
    (set, get) => ({
      activeProcesses: new Map(),
      completedProcesses: [],
      isExpanded: false,

      addProcess: (info) => {
        set((state) => {
          const newMap = new Map(state.activeProcesses);
          newMap.set(info.id, info);
          return {
            activeProcesses: newMap,
            // Auto-expand when new process starts
            isExpanded: true
          };
        });
      },

      updateProcess: (id, updates) => {
        set((state) => {
          const newMap = new Map(state.activeProcesses);
          const existing = newMap.get(id);

          if (!existing) return state;

          const updated = { ...existing, ...updates };
          newMap.set(id, updated);

          // If process completed or failed, move to completed after a delay
          if (updated.status === 'completed' || updated.status === 'failed' || updated.status === 'timeout') {
            // Keep in active for a few seconds so user sees completion
            setTimeout(() => {
              set((state) => {
                const activeMap = new Map(state.activeProcesses);
                const process = activeMap.get(id);

                if (process) {
                  activeMap.delete(id);

                  // Add to completed (keep last 5)
                  const completed = [process, ...state.completedProcesses].slice(0, 5);

                  return {
                    activeProcesses: activeMap,
                    completedProcesses: completed
                  };
                }

                return state;
              });
            }, 5000); // Remove after 5 seconds
          }

          return { activeProcesses: newMap };
        });
      },

      removeProcess: (id) => {
        set((state) => {
          const newMap = new Map(state.activeProcesses);
          const removed = newMap.get(id);
          newMap.delete(id);

          // Add to completed if not already there
          const completed = removed
            ? [removed, ...state.completedProcesses].slice(0, 5)
            : state.completedProcesses;

          return {
            activeProcesses: newMap,
            completedProcesses: completed
          };
        });
      },

      getElapsedTime: (id) => {
        const process = get().activeProcesses.get(id);
        if (!process) return 0;
        return Date.now() - process.startedAt;
      },

      toggleExpanded: () => {
        set((state) => ({ isExpanded: !state.isExpanded }));
      },

      clearCompleted: () => {
        set({ completedProcesses: [] });
      }
    }),
    {
      name: 'process-monitor-storage',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const data = JSON.parse(str);
          // Convert stored array back to Map
          if (data.state.activeProcesses) {
            data.state.activeProcesses = new Map(data.state.activeProcesses);
          }
          return data;
        },
        setItem: (name, value) => {
          const data = {
            ...value,
            state: {
              ...value.state,
              // Convert Map to array for JSON serialization
              activeProcesses: Array.from(value.state.activeProcesses.entries())
            }
          };
          localStorage.setItem(name, JSON.stringify(data));
        },
        removeItem: (name) => localStorage.removeItem(name)
      }
    }
  )
);
